import React, { useState } from "react";
import {
  Typography,
  Paper,
  Box,
  Grid,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Dialog,
  DialogContent,
  IconButton,
  Chip,
  Card,
  CardContent,
} from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CloseIcon from "@mui/icons-material/Close";
import AutorenewIcon from "@mui/icons-material/Autorenew";
import SpeedIcon from "@mui/icons-material/Speed";
import SchoolIcon from "@mui/icons-material/School";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import InsightsIcon from "@mui/icons-material/Insights";
import AnalyticsIcon from "@mui/icons-material/Analytics";

const HomeworkDigitization = () => {
  const [openImage, setOpenImage] = useState(null);

  // Project data
  const project = {
    title: "AI-Powered Homework Digitization",
    subtitle: "Automating Legacy Content Migration",
    overview:
      "An AI-powered homework digitization system that automates the conversion of traditional DOCX-formatted homework materials into structured digital formats for our government education platform. This tool addresses a critical pain point in the digital transformation of education by dramatically reducing the time and effort required to migrate legacy teaching materials to online learning environments.",
    challenges: [
      "Traditional homework in DOCX format needed manual conversion to online question formats",
      "The platform's question type frameworks had specific structural requirements",
      "Manual input was time-consuming and error-prone",
      "Teachers struggled to determine how to best adapt their materials to available digital formats",
      "The backlog of materials to digitize was growing faster than manual processing could handle",
    ],
    technicalSolution: [
      "Ingests DOCX files uploaded by teachers",
      "Analyzes the document structure, content, and question types",
      "Maps traditional question formats to appropriate digital equivalents",
      "Restructures content to fit platform requirements",
      "Populates the backend system with properly formatted questions",
      "Flags edge cases requiring human review",
    ],
    implementationProcess: [
      "Extensive analysis of existing homework formats and patterns",
      "Creation of a comprehensive mapping between traditional and digital question types",
      "Development of robust error handling and confidence scoring",
      "Implementation of an intuitive review interface for teachers",
      "Iterative testing with real-world documents to improve accuracy",
    ],
    results: [
      {
        metric: "70%",
        description: "Increase in available digital questions on the platform",
      },
      {
        metric: "Significant",
        description: "Reduction in teacher time spent on manual data entry",
      },
      {
        metric: "Faster",
        description: "Adoption of the online platform by participating schools",
      },
      {
        metric: "Improved",
        description: "Teacher satisfaction with the digital transition process",
      },
    ],
    images: [
      {
        src: "/images/homework/homework_input.png",
        title: "Original DOCX Homework",
        description:
          "Traditional document format that teachers need to digitize",
      },
      {
        src: "/images/homework/homework_workflow.png",
        title: "Multi-Agent Workflow",
        description:
          "The AI pipeline that processes and transforms document content",
      },
      {
        src: "/images/homework/output1.png",
        title: "Digitized Output Example 1",
        description:
          "Traditional worksheet transformed into interactive digital format",
      },
      {
        src: "/images/homework/output2.png",
        title: "Digitized Output Example 2",
        description:
          "Complex question types properly converted to platform format",
      },
    ],
    video: "/videos/homework_video.mp4",
  };

  // Image modal handlers
  const handleImageClick = (img) => setOpenImage(img);
  const handleClose = () => setOpenImage(null);

  return (
    <>
      {/* Image Lightbox Dialog */}
      <Dialog open={!!openImage} onClose={handleClose} maxWidth="md" fullWidth>
        <DialogContent sx={{ p: 0, position: "relative", bgcolor: "#111" }}>
          <IconButton
            aria-label="close"
            onClick={handleClose}
            sx={{
              position: "absolute",
              top: 8,
              right: 8,
              color: "#fff",
              zIndex: 1,
            }}
          >
            <CloseIcon fontSize="large" />
          </IconButton>
          {openImage && (
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                p: 2,
              }}
            >
              <img
                src={openImage.src}
                alt={openImage.title}
                style={{
                  maxWidth: "100%",
                  maxHeight: "80vh",
                  width: "auto",
                  height: "auto",
                  borderRadius: 8,
                  boxShadow: "0 4px 32px #000a",
                  background: "#222",
                }}
              />
              <Typography variant="h6" color="#fff" sx={{ mt: 2 }}>
                {openImage.title}
              </Typography>
              <Typography variant="body2" color="#ccc" sx={{ mt: 1, mb: 1 }}>
                {openImage.description}
              </Typography>
            </Box>
          )}
        </DialogContent>
      </Dialog>

      {/* Project Header */}
      <Paper
        elevation={3}
        sx={{
          p: 4,
          mb: 4,
          background: "linear-gradient(135deg, #00695c 0%, #009688 100%)",
          color: "white",
        }}
      >
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12}>
            <Typography variant="h4" gutterBottom fontWeight="bold">
              {project.title}
            </Typography>
            <Typography variant="h6" gutterBottom sx={{ mb: 2, opacity: 0.9 }}>
              {project.subtitle}
            </Typography>
            <Typography variant="body1" paragraph sx={{ fontSize: "1.1rem" }}>
              {project.overview}
            </Typography>
            <Box sx={{ mt: 2 }}>
              <Chip
                icon={<SchoolIcon />}
                label="Education Technology"
                sx={{ mr: 1, mb: 1, bgcolor: "rgba(255,255,255,0.15)" }}
              />
              <Chip
                icon={<AutorenewIcon />}
                label="Digital Transformation"
                sx={{ mr: 1, mb: 1, bgcolor: "rgba(255,255,255,0.15)" }}
              />
              <Chip
                icon={<AnalyticsIcon />}
                label="Multi-Agent AI"
                sx={{ mr: 1, mb: 1, bgcolor: "rgba(255,255,255,0.15)" }}
              />
            </Box>
          </Grid>
        </Grid>
      </Paper>

      {/* Demo Video */}
      <Paper elevation={3} sx={{ p: 4, mb: 4, bgcolor: "#263238" }}>
        <Typography variant="h5" gutterBottom sx={{ mb: 3, color: "white" }}>
          System Demo
        </Typography>
        <Box sx={{ width: "100%", display: "flex", justifyContent: "center" }}>
          <video
            controls
            style={{
              width: "100%",
              maxWidth: 800,
              borderRadius: 8,
              background: "#000",
              display: "block",
            }}
          >
            <source src={project.video} type="video/mp4" />
            Your browser does not support the video tag.
          </video>
        </Box>
      </Paper>

      {/* The Challenge */}
      <Paper
        elevation={3}
        sx={{ p: 4, mb: 4, bgcolor: "#263238", color: "white" }}
      >
        <Typography variant="h5" gutterBottom sx={{ mb: 2 }}>
          The Challenge
        </Typography>
        <Typography variant="body1" paragraph>
          Teachers participating in our government education project faced
          significant barriers when trying to digitize their existing teaching
          materials:
        </Typography>
        <List>
          {project.challenges.map((challenge, index) => (
            <ListItem key={index}>
              <ListItemIcon>
                <CheckCircleIcon sx={{ color: "#ff5722" }} />
              </ListItemIcon>
              <ListItemText primary={challenge} sx={{ color: "white" }} />
            </ListItem>
          ))}
        </List>
      </Paper>

      {/* Technical Solution with Workflow */}
      <Paper
        elevation={3}
        sx={{ p: 4, mb: 4, bgcolor: "#263238", color: "white" }}
      >
        <Typography variant="h5" gutterBottom sx={{ mb: 3 }}>
          Technical Solution
        </Typography>

        <Grid container spacing={4}>
          <Grid item xs={12} md={6}>
            <Typography variant="body1" paragraph>
              I designed and implemented a multi-agent AI pipeline that:
            </Typography>
            <List>
              {project.technicalSolution.map((step, index) => (
                <ListItem key={index}>
                  <ListItemIcon>
                    <Box
                      sx={{
                        width: 28,
                        height: 28,
                        borderRadius: "50%",
                        bgcolor: "#009688",
                        color: "white",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        fontWeight: "bold",
                      }}
                    >
                      {index + 1}
                    </Box>
                  </ListItemIcon>
                  <ListItemText primary={step} sx={{ color: "white" }} />
                </ListItem>
              ))}
            </List>
            <Typography variant="body1" paragraph sx={{ mt: 2 }}>
              The system employs multiple specialized AI agents working in
              sequence to handle different aspects of the conversion process,
              from document parsing to semantic understanding of question
              intent.
            </Typography>
          </Grid>

          <Grid item xs={12} md={6}>
            <Typography variant="h6" gutterBottom>
              Multi-Agent Workflow
            </Typography>
            <Box
              sx={{
                cursor: "pointer",
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
              }}
              onClick={() => handleImageClick(project.images[1])}
            >
              <img
                src={project.images[1].src}
                alt={project.images[1].title}
                style={{
                  width: "100%",
                  maxHeight: "300px",
                  objectFit: "contain",
                  borderRadius: "8px",
                }}
              />
            </Box>
            <Typography
              variant="body2"
              sx={{
                color: "rgba(255,255,255,0.7)",
                mt: 2,
                textAlign: "center",
              }}
            >
              The AI pipeline processes and transforms document content through
              multiple specialized agents
            </Typography>
          </Grid>
        </Grid>
      </Paper>

      {/* Input and Output Examples */}
      <Paper
        elevation={3}
        sx={{ p: 4, mb: 4, bgcolor: "#263238", color: "white" }}
      >
        <Typography variant="h5" gutterBottom sx={{ mb: 3 }}>
          Transformation Examples
        </Typography>
        <Grid container spacing={3}>
          {/* Input Example */}
          <Grid item xs={12} md={4}>
            <Box
              sx={{
                cursor: "pointer",
                height: "100%",
                display: "flex",
                flexDirection: "column",
              }}
              onClick={() => handleImageClick(project.images[0])}
            >
              <Box sx={{ flex: 1 }}>
                <img
                  src={project.images[0].src}
                  alt={project.images[0].title}
                  style={{
                    width: "100%",
                    borderRadius: "8px",
                    boxShadow: "0 2px 10px rgba(0,0,0,0.3)",
                  }}
                />
              </Box>
              <Typography variant="h6" sx={{ mt: 2 }}>
                {project.images[0].title}
              </Typography>
              <Typography
                variant="body2"
                sx={{ color: "rgba(255,255,255,0.7)" }}
              >
                {project.images[0].description}
              </Typography>
            </Box>
          </Grid>

          {/* Output Examples */}
          <Grid item xs={12} md={8}>
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <Box
                  sx={{
                    cursor: "pointer",
                    height: "100%",
                    display: "flex",
                    flexDirection: "column",
                  }}
                  onClick={() => handleImageClick(project.images[2])}
                >
                  <Box sx={{ flex: 1 }}>
                    <img
                      src={project.images[2].src}
                      alt={project.images[2].title}
                      style={{
                        width: "100%",
                        borderRadius: "8px",
                        boxShadow: "0 2px 10px rgba(0,0,0,0.3)",
                      }}
                    />
                  </Box>
                  <Typography variant="h6" sx={{ mt: 2 }}>
                    {project.images[2].title}
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{ color: "rgba(255,255,255,0.7)" }}
                  >
                    {project.images[2].description}
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={12} md={6}>
                <Box
                  sx={{
                    cursor: "pointer",
                    height: "100%",
                    display: "flex",
                    flexDirection: "column",
                  }}
                  onClick={() => handleImageClick(project.images[3])}
                >
                  <Box sx={{ flex: 1 }}>
                    <img
                      src={project.images[3].src}
                      alt={project.images[3].title}
                      style={{
                        width: "100%",
                        borderRadius: "8px",
                        boxShadow: "0 2px 10px rgba(0,0,0,0.3)",
                      }}
                    />
                  </Box>
                  <Typography variant="h6" sx={{ mt: 2 }}>
                    {project.images[3].title}
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{ color: "rgba(255,255,255,0.7)" }}
                  >
                    {project.images[3].description}
                  </Typography>
                </Box>
              </Grid>
            </Grid>
          </Grid>
        </Grid>
        <Box
          sx={{ mt: 3, p: 3, bgcolor: "rgba(0,150,136,0.2)", borderRadius: 2 }}
        >
          <Typography variant="body1" align="center">
            The AI system intelligently converts traditional document formats
            into structured digital content while preserving the educational
            intent of each question.
          </Typography>
        </Box>
      </Paper>

      {/* Implementation Process */}
      <Paper
        elevation={3}
        sx={{ p: 4, mb: 4, bgcolor: "#263238", color: "white" }}
      >
        <Typography variant="h5" gutterBottom sx={{ mb: 3 }}>
          Implementation Process
        </Typography>
        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <Typography variant="body1" paragraph>
              The development of this tool required:
            </Typography>
            <List>
              {project.implementationProcess.map((process, index) => (
                <ListItem key={index}>
                  <ListItemIcon>
                    <CheckCircleIcon sx={{ color: "#4caf50" }} />
                  </ListItemIcon>
                  <ListItemText primary={process} sx={{ color: "white" }} />
                </ListItem>
              ))}
            </List>
          </Grid>
          <Grid item xs={12} md={6}>
            <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
              <Card
                elevation={2}
                sx={{ bgcolor: "rgba(255,255,255,0.1)", color: "white" }}
              >
                <CardContent
                  sx={{ display: "flex", alignItems: "center", gap: 2 }}
                >
                  <UploadFileIcon fontSize="large" sx={{ color: "#4caf50" }} />
                  <Box>
                    <Typography variant="h6">Document Analysis</Typography>
                    <Typography
                      variant="body2"
                      sx={{ color: "rgba(255,255,255,0.7)" }}
                    >
                      Advanced parsing of DOCX structure and content semantics
                    </Typography>
                  </Box>
                </CardContent>
              </Card>
              <Card
                elevation={2}
                sx={{ bgcolor: "rgba(255,255,255,0.1)", color: "white" }}
              >
                <CardContent
                  sx={{ display: "flex", alignItems: "center", gap: 2 }}
                >
                  <AutorenewIcon fontSize="large" sx={{ color: "#4caf50" }} />
                  <Box>
                    <Typography variant="h6">Format Transformation</Typography>
                    <Typography
                      variant="body2"
                      sx={{ color: "rgba(255,255,255,0.7)" }}
                    >
                      Intelligent mapping between traditional and digital
                      formats
                    </Typography>
                  </Box>
                </CardContent>
              </Card>
              <Card
                elevation={2}
                sx={{ bgcolor: "rgba(255,255,255,0.1)", color: "white" }}
              >
                <CardContent
                  sx={{ display: "flex", alignItems: "center", gap: 2 }}
                >
                  <InsightsIcon fontSize="large" sx={{ color: "#4caf50" }} />
                  <Box>
                    <Typography variant="h6">Error Handling</Typography>
                    <Typography
                      variant="body2"
                      sx={{ color: "rgba(255,255,255,0.7)" }}
                    >
                      Robust detection and flagging of edge cases for review
                    </Typography>
                  </Box>
                </CardContent>
              </Card>
            </Box>
          </Grid>
        </Grid>
      </Paper>

      {/* Results and Impact */}
      <Paper
        elevation={3}
        sx={{
          p: 4,
          background: "linear-gradient(135deg, #00695c 0%, #009688 100%)",
          color: "white",
        }}
      >
        <Typography variant="h5" gutterBottom sx={{ mb: 3 }}>
          Results and Impact
        </Typography>
        <Typography variant="body1" paragraph>
          The automated digitization tool has transformed our content migration
          process:
        </Typography>
        <Grid container spacing={3}>
          {project.results.map((result, index) => (
            <Grid item xs={12} sm={6} md={3} key={index}>
              <Box
                sx={{
                  p: 3,
                  textAlign: "center",
                  bgcolor: "rgba(255,255,255,0.1)",
                  borderRadius: 2,
                  height: "100%",
                  display: "flex",
                  flexDirection: "column",
                  justifyContent: "center",
                }}
              >
                <Typography variant="h4" fontWeight="bold" gutterBottom>
                  {result.metric}
                </Typography>
                <Typography variant="body1">{result.description}</Typography>
              </Box>
            </Grid>
          ))}
        </Grid>
        <Box sx={{ mt: 3 }}>
          <Typography variant="body1">
            The system has significantly improved the efficiency of content
            migration, enabling teachers to focus on creating and improving
            educational materials rather than manual data entry.
          </Typography>
        </Box>
      </Paper>
    </>
  );
};

export default HomeworkDigitization;
